/**
 * StatusBadge — color-coded pill for conversation status (Story 7.10).
 *
 * Status palette (per AC3):
 *   - active / awaiting_response → blue / gray
 *   - closed → dark slate
 *   - converted → green
 *   - error → red
 *   - everything else → muted fallback
 *
 * Purely presentational; no click handlers. Keeps the conversations list +
 * detail header consistent.
 */

export type ConversationStatus =
  | 'scheduled'
  | 'awaiting_response'
  | 'active'
  | 'scheduled_callback'
  | 'converted'
  | 'declined'
  | 'inactive'
  | 'closed'
  | 'error'
  // Story 12.1 / Epic E12 — pausa controlada pelo operador (takeover humano).
  | 'human_handoff';

const STATUS_PALETTE: Record<ConversationStatus, string> = {
  scheduled: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-300',
  awaiting_response: 'bg-muted text-muted-foreground',
  active: 'bg-blue-500/20 text-blue-700 dark:text-blue-300',
  scheduled_callback: 'bg-purple-500/20 text-purple-700 dark:text-purple-300',
  converted: 'bg-green-500/20 text-green-700 dark:text-green-300',
  declined: 'bg-orange-500/20 text-orange-700 dark:text-orange-300',
  inactive: 'bg-muted text-muted-foreground',
  closed: 'bg-slate-700 text-slate-100 dark:bg-slate-800 dark:text-slate-200',
  error: 'bg-red-500/20 text-red-700 dark:text-red-300',
  human_handoff: 'bg-amber-500/25 text-amber-800 dark:text-amber-200',
};

export function StatusBadge({ status }: { status: ConversationStatus }) {
  const palette = STATUS_PALETTE[status] ?? 'bg-muted text-muted-foreground';
  return (
    <span
      data-testid={`conversation-status-badge-${status}`}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${palette}`}
    >
      {status}
    </span>
  );
}
